import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CircleNotch } from '@phosphor-icons/react';
import { getVisit } from '@/lib/api/routes/visits';

/**
 * Canonical visit link handler (/visits/:visitId).
 *
 * A visit is viewed on its customer's page, but the canonical path only carries
 * the visit id. Resolve the owning customer, then redirect to the customer page
 * with the visit selected.
 *
 * Kept separate from EntityPathRedirect — and keyed off useParams rather than
 * the location — so that while the lazy CustomerDetails route is loading and
 * this component briefly lingers, a re-fire can only reproduce the same
 * customer+visit URL, never fall through to a bare customer route that would
 * strip the visit selection.
 */
export function VisitRedirect() {
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();

  const { data, isError } = useQuery({
    queryKey: ['visit', 'full', visitId],
    queryFn: () => getVisit(visitId!),
    enabled: !!visitId,
    staleTime: 30000,
    // A bad/stale link should fall through to a graceful redirect promptly,
    // not sit on a spinner through the default retry backoff.
    retry: false,
  });

  useEffect(() => {
    if (!visitId) return;
    if (isError) {
      navigate('/assistant', { replace: true });
      return;
    }
    const customerId = data?.visit.customerId;
    if (customerId) {
      navigate(`/customers/${customerId}?visitId=${visitId}`, { replace: true });
    }
  }, [visitId, data, isError, navigate]);

  return (
    <div className="flex items-center justify-center h-screen text-stone-400 gap-2">
      <CircleNotch className="h-4 w-4 animate-spin" />
      <span className="text-sm">Loading…</span>
    </div>
  );
}
